import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import { deleteGoal, getGoals } from "../utils/methods";
import { strings } from "../utils";
import { Button } from "../components";
import ModalScreen from "../components/modal";
import DeleteIcon from "../components/Icons/deleteIcon.jsx";
import usePersistData from "../store/usePersistData.js";
import { getAuth } from "firebase/auth";

const GoalsTable = ({ userId }) => {
  const { user } = usePersistData();
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [id, setId] = useState(0);
  const [filter, setFilter] = useState("all");
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const usersPerPage = 10;

  useEffect(() => {
    const userUid = user?.uid || getAuth().currentUser?.uid;
    setId(userUid);

    const fetchGoals = async () => {
      setLoading(true);
      try {
        let userGoals = await getGoals(user);

        if (filter === "ownCreated") {
          userGoals = userGoals.filter((goal) => goal.managerId === id);
        } else if (filter === "managerCreated") {
          userGoals = userGoals.filter((goal) => goal.managerId !== id);
        }

        const sortedGoals = userGoals.sort((a, b) => {
          return new Date(b.createdAt) - new Date(a.createdAt);
        });
        setGoals(sortedGoals);
      } catch (error) {
        console.error("Error fetching goals:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchGoals();
  }, [userId, user, refreshKey, filter]);

  const handleAddGoal = () => {
    navigate(`/goalsOrOkHours/edit/new`);
    setRefreshKey((prevKey) => prevKey + 1);
  };

  const handleClickOpen = (goal) => {
    setSelectedGoal(goal);
    setOpenDialog(true);
  };

  const handleClose = () => {
    setOpenDialog(false);
    setSelectedGoal(null);
  };

  const handleDeleteGoal = async () => {
    try {
      if (selectedGoal?.id) {
        await deleteGoal(selectedGoal.id);
        setRefreshKey((prevKey) => prevKey + 1);
      }
      handleClose();
    } catch (error) {
      console.error("Error deleting goal:", error);
    }
  };

  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentGoals = goals.slice(indexOfFirstUser, indexOfLastUser);
  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  const totalPages = Math.ceil(goals.length / usersPerPage);

  return (
    <div className="mt-4 mx-8 flex flex-col justify-end">
      <div className="flex justify-between mb-8">
        <p className="text-[32px] font-semibold">{strings["goals"]}</p>

        <div className="flex justify-between mb-2">
          {user?.managedEmployees?.length > 0 && (
            <Button onClick={handleAddGoal}>{strings["addGoalTitle"]}</Button>
          )}
        </div>
      </div>

      <div className="mb-4 flex justify-end">
        <FormControl>
          <Select value={filter} onChange={(e) => setFilter(e.target.value)}>
            <MenuItem value="all">All Goals</MenuItem>
            <MenuItem value="ownCreated">Own Created</MenuItem>
            <MenuItem value="managerCreated">Manager Created</MenuItem>
          </Select>
        </FormControl>
      </div>
      <div className="mt-2 flex justify-end mb-2">
        <button
          onClick={() => paginate(currentPage - 1)}
          disabled={currentPage === 1}
          className={`px-4 py-2 underline text-black font-bold ${
            currentPage === 1 ? "text-gray-400 cursor-not-allowed" : ""
          }`}
        >
          Previous
        </button>

        <span className="px-4 py-2">{`Page ${currentPage} of ${totalPages}`}</span>
        <button
          onClick={() => paginate(currentPage + 1)}
          disabled={currentPage === totalPages}
          className={`px-4 py-2 underline text-black font-bold ${
            currentPage === totalPages ? "text-gray-400 cursor-not-allowed" : ""
          }`}
        >
          Next
        </button>
      </div>

      <TableContainer component={Paper} sx={{ border: "2px solid border" }}>
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell align="center">Goal Title</TableCell>
              <TableCell align="center">Employee</TableCell>
              <TableCell align="center">Progress (%)</TableCell>
              <TableCell align="center">Goal Type</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : currentGoals.length > 0 ? (
              currentGoals.map((goal, index) => (
                <TableRow
                  key={index}
                  hover={user.designation === "EMPLOYEE"}
                  onClick={(e) => {
                    {
                      goal?.managerId === id
                        ? navigate(`/goalsDetails/${goal?.managerId}`, {
                            state: { goalIdk: goal?.id },
                          })
                        : navigate(`/goalsDetails/${goal?.empId}`, {
                            state: { goalIdk: goal?.id },
                          });
                    }
                    e.stopPropagation();
                  }}
                  className={`${
                    user.designation === "EMPLOYEE" ? "cursor-pointer" : null
                  }`}
                >
                  <TableCell align="center">{goal?.title || "N/A"}</TableCell>
                  <TableCell align="center">
                    {/* {goal?.employeeName || "N/A"}
                    <br /> */}
                    {goal?.email}
                  </TableCell>

                  <TableCell align="center">{goal?.progress || "0"}%</TableCell>

                  <TableCell align="center">
                    {goal?.managerId === id ? "Own Created" : "Manager Created"}
                  </TableCell>

                  <TableCell align="center">
                    <div className="flex items-center justify-center gap-x-2">
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/goalsOrOkHours/edit/${goal?.id}`);
                        }}
                      >
                        Edit
                      </Button>

                      <Button
                        variant="outlineArrow"
                        color="secondary"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleClickOpen(goal);
                        }}
                      >
                        Delete
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  No goals found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <div className="mt-4 flex justify-end mb-8">
        <button
          onClick={() => paginate(currentPage - 1)}
          disabled={currentPage === 1}
          className={`px-4 py-2 underline text-black font-bold ${
            currentPage === 1 ? "text-gray-400 cursor-not-allowed" : ""
          }`}
        >
          Previous
        </button>

        <span className="px-4 py-2">{`Page ${currentPage} of ${totalPages}`}</span>
        <button
          onClick={() => paginate(currentPage + 1)}
          disabled={currentPage === totalPages}
          className={`px-4 py-2 underline text-black font-bold ${
            currentPage === totalPages ? "text-gray-400 cursor-not-allowed" : ""
          }`}
        >
          Next
        </button>
      </div>

      <ModalScreen
        open={openDialog}
        onClose={handleClose}
        onConfirm={handleDeleteGoal}
        title={strings["deleteGoal"]}
        description={strings["deleteGoalDesc"]}
        confirmButtonText={strings["DeleteBtn"]}
        cancelButtonText={strings["cancelBtn"]}
        hideClass="hidden"
        icon={DeleteIcon}
      />
    </div>
  );
};

export default GoalsTable;
